import { ReactNode } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AwardProgressBadge } from "../../../../../common/components/AwardProgressBadge/AwardProgressBadge";
import { IconArrowLink } from "../../../../../common/icons/IconArrowLink/IconArrowLink";
import { AWARD_PROGRESS } from "../../../../../common/enums/award-progress-codes";
import defaultAwardContent from "../../../../../content/awardLabels";
import { trackIncentiveTileClick } from "../../../../../common/tealium/PerformanceIncomeDashboard/PerformanceIncomeDashboardTrack";
import "./PerformanceTileHeader.scss";

interface Props {
  label: ReactNode;
  value?: string | number;
  link?: string;
  labelLink?: string;
  tag?: AWARD_PROGRESS;
  showArrow?: boolean;
  tealiumTag?: string;
  subtitle?: ReactNode;
  action?: ReactNode;
}

const PERFORMANCE_TILE_HEADER_ID = "__PERFORMANCE_TILE_HEADER__";

export const PerformanceTileHeader = ({
  label,
  value,
  link,
  labelLink,
  tag,
  showArrow,
  tealiumTag,
  subtitle,
  action,
}: Props) => {
  const { t } = useTranslation(["awardLabels"]);
  const noDataLabel = t(
    "awardLabels.no-data-available",
    defaultAwardContent.awardLabels["no-data-available"] as string,
  );
  const isNoData = value === "--";

  const handleTealiumTrack = () => {
    if (tealiumTag) {
      trackIncentiveTileClick(tealiumTag);
    }
  };

  const renderValue = () =>
    value !== undefined ? (
      <span className="perf-tile-header__value" {...(isNoData ? { role: "text", "aria-label": noDataLabel } : {})}>
        {isNoData ? <span aria-hidden="true">{value}</span> : value}
      </span>
    ) : null;

  const labelContent = (
    <>
      <span className="perf-tile-header__label">
        {label}
        {action}
      </span>
      <div className="perf-tile-header__right">
        {renderValue()}
        {tag && <AwardProgressBadge status={tag} hideIcon />}
        {(showArrow || link || labelLink) && <IconArrowLink weight="medium" />}
      </div>
    </>
  );

  if (link) {
    // Handle hash-based modal links without navigation
    if (link.startsWith("#")) {
      return (
        <div className="perf-tile-header__container">
          <a
            href={link}
            className="perf-tile-header perf-tile-header--link"
            onClick={(e) => {
              e.preventDefault();
              handleTealiumTrack();
              // Set hash without scrolling by using history API
              window.history.pushState(null, "", link);
              // Manually trigger hashchange event
              window.dispatchEvent(new HashChangeEvent("hashchange"));
            }}
          >
            {labelContent}
          </a>
          {subtitle && <div className="perf-tile-header__subtitle">{subtitle}</div>}
        </div>
      );
    }
    return (
      <div className="perf-tile-header__container">
        <Link to={link} className="perf-tile-header perf-tile-header--link" onClick={handleTealiumTrack}>
          {labelContent}
        </Link>
        {subtitle && <div className="perf-tile-header__subtitle">{subtitle}</div>}
      </div>
    );
  }

  if (labelLink) {
    return (
      <div className="perf-tile-header__container">
        <Link to={labelLink} className="perf-tile-header perf-tile-header--link" onClick={handleTealiumTrack}>
          <span className="perf-tile-header__label perf-tile-header__label--link">{label}</span>
          <div className="perf-tile-header__right">
            {renderValue()}
            {tag && <AwardProgressBadge status={tag} hideIcon />}
            <IconArrowLink weight="medium" />
          </div>
        </Link>
        {subtitle && <div className="perf-tile-header__subtitle">{subtitle}</div>}
      </div>
    );
  }

  return (
    <div className="perf-tile-header__container">
      <div className="perf-tile-header">{labelContent}</div>
      {subtitle && <div className="perf-tile-header__subtitle">{subtitle}</div>}
    </div>
  );
};

type PerformanceTileHeaderWithId = typeof PerformanceTileHeader & { __PERFORMANCE_TILE_ID: string };
(PerformanceTileHeader as PerformanceTileHeaderWithId).__PERFORMANCE_TILE_ID = PERFORMANCE_TILE_HEADER_ID;
